import React, { useEffect, useState, useMemo, lazy, Suspense, useCallback } from "react";
import { startOfDay, endOfDay, isAfter, isEqual } from "date-fns";
import { Text, Flex, SegmentedControl, Progress } from "@radix-ui/themes";
import { DatePickerWithRange, MetricCard, ChartCard, LocationMultiSelect, LiveConnectionsCard, BrandMultiSelect } from "components";
import { useLiveConnectionStats, useMetricsData, useGetAllBrands } from "hooks";
import { useProfileContext, useLocationContext, useDateRangeContext } from "context";
import { connectionsChartConfig } from "../../configs/ConnectionsChartConfig";
import { durationsChartConfig } from "../../configs/DurationsChartConfig";
import { MultiSelect } from "components/multiSelect/MultiSelect";
import { FilterByMultiSelect } from "components/multiSelect/FilterByMultiSelect";
import { DisplayBySelect } from "components/select/DisplayBySelect";
import { ZonesMultiSelect } from "components/zonesMultiSelect/ZonesMultiSelect";
import { useGetZones } from "hooks";
import { useOutletContext } from "react-router-dom";


const UniqueGuestsByAgeChart = lazy(() => 
  import("components/charts/UniqueGuestsByAgeChart").then(module => ({ default: module.UniqueGuestsByAgeChart }))
);

const UniqueGuestsByGenderChart = lazy(() => 
  import("components/charts/UniqueGuestsByGenderChart").then(module => ({ default: module.UniqueGuestsByGenderChart }))
);

const AverageDurationByAgeChart = lazy(() => 
  import("components/charts/AverageDurationByAgeChart").then(module => ({ default: module.AverageDurationByAgeChart }))
);

const AverageDurationByGenderChart = lazy(() => 
  import("components/charts/AverageDurationByGenderChart").then(module => ({ default: module.AverageDurationByGenderChart }))
);

const filterOptions = [
  { label: "Customer loyalty", value: "guest_status" },
  { label: "Age", value: "age" },
  { label: "Gender", value: "gender" },
];

const displayOptions = [
  { label: "Default", value: "default" },
  { label: "Customer loyalty", value: "guest_status" },
  { label: "Age", value: "age" },
  { label: "Gender", value: "gender" },
];

const loyaltyOptions = [
  { label: "New customers", value: "new" },
  { label: "Returning customers", value: "returning" },
];

const genderOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "others" },
  { label: "No Response", value: "field_disabled" },
];

const ageOptions = [
  { label: "Under 18", value: "Under 18" },
  { label: "18-24", value: "18-24" },
  { label: "25-34", value: "25-34" },
  { label: "35-44", value: "35-44" },
  { label: "45-54", value: "45-54" },
  { label: "55-64", value: "55-64" },
  { label: "65+", value: "65+" },
  { label: "No Response", value: "field_disabled" },
];

export default function Dashboard() {
  const { userProfile } = useProfileContext();
  const { initialItems, locationSelection, setLocationSelection, isLocationsSuccess } = useLocationContext();
  const { selectedDateRange, setSelectedDateRange, setActivePage } = useDateRangeContext();

  // Set active page to dashboard for page-specific date range
  useEffect(() => {
    setActivePage('dashboard');
  }, [setActivePage]);

  // Custom hooks
  const { liveConnectionStat } = useLiveConnectionStats(locationSelection);

  const [disableBefore, setDisableBefore] = useState(null);
  const [displayBy, setDisplayBy] = useState("default");
  const [zoneSelection, setZoneSelection] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterSelection, setFilterSelection] = useState([]);

  // State for brand selection and filtering
  const [brandSelection, setBrandSelection] = useState([]);
  const [showBrandFilter, setShowBrandFilter] = useState(false);
  const [filteredLocationOptions, setFilteredLocationOptions] = useState(initialItems || []);

  // Get brands data using your existing hook
  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);

  // Format brands for dropdown
  const formattedBrandOptions = useMemo(() => {
    if (!brandsData || brandsData.length === 0) return [];

    return brandsData.map((brand) => ({
      label: brand.is_default ? "No brand" : brand.name,
      value: brand.id.toString(),
      locations: brand.locations || [],
      isDefault: brand.is_default,
    }));
  }, [brandsData]);

  // Determine if we should show brand filter
  useEffect(() => {
    // Only show brand filter if there's more than one brand
    if (formattedBrandOptions.length > 1) {
      setShowBrandFilter(true);
      // Initially select all brands
      const allBrandIds = formattedBrandOptions.map((brand) => brand.value);
      setBrandSelection(allBrandIds);
    } else {
      setShowBrandFilter(false);
    }
  }, [formattedBrandOptions]);

  // Filter locations based on selected brands
  useEffect(() => {
    // Skip if we don't have brand options or brand filter isn't shown
    if (!showBrandFilter || formattedBrandOptions.length <= 1) {
      setFilteredLocationOptions(initialItems);
      return;
    }

    // If no brands selected, show no locations
    if (brandSelection.length === 0) {
      setFilteredLocationOptions([]);
      setLocationSelection([]);
      return;
    }

    // Collect all locations from selected brands
    const selectedLocations = new Set();

    formattedBrandOptions
      .filter((brand) => brandSelection.includes(brand.value))
      .forEach((brand) => {
        if (brand.locations && Array.isArray(brand.locations)) {
          brand.locations.forEach((location) => {
            selectedLocations.add(location.id.toString());
          });
        }
      });

    // Convert to array
    const selectedLocationIds = Array.from(selectedLocations);

    // Filter initialItems to only include locations from selected brands
    const filtered = initialItems.filter((item) => selectedLocationIds.includes(String(item.value)));

    // Update filtered locations
    setFilteredLocationOptions(filtered);

    // IMPROVED SELECTION LOGIC:
    // 1. Check if we have all brands selected
    const allBrandsSelected = brandSelection.length === formattedBrandOptions.length;

    // 2. Check if all available locations are selected
    const allLocationsSelected = filtered.length === locationSelection.length && filtered.every((item) => locationSelection.includes(String(item.value)));

    // If all brands are selected, select all available locations
    if (allBrandsSelected || (brandSelection.length > 0 && !allLocationsSelected)) {
      // Always select all available locations when all brands are selected
      // or when the available locations don't match the current selection
      setLocationSelection(selectedLocationIds);
    } else if (filtered.length === 0) {
      // No locations available
      setLocationSelection([]);
    }
    // Otherwise keep the current selection
  }, [brandSelection, formattedBrandOptions, initialItems, showBrandFilter]);

  // Update selection based on changes to selectedCategories
  useEffect(() => {
    const deselectedCategories = filterSelection.filter((cat) => !selectedCategories.includes(cat));

    if (deselectedCategories.length > 0) {
      // Remove deselected filters from the UI
      const remainingSelections = filterSelection.filter((selection) => {
        if (deselectedCategories.includes("guest_status")) {
          return !selection.includes("guest_status");
        }
        return !deselectedCategories.includes(selection);
      });

      // Reset deselected filters to their default values
      deselectedCategories.forEach((category) => {
        switch (category) {
          case "guest_status":
            setLoyaltySelection(loyaltyOptions.map((option) => option.value));
            break;
          case "age":
            setAgeSelection(ageOptions.map((option) => option.value));
            break;
          case "gender":
            setGenderSelection(genderOptions.map((option) => option.value));
            break;
        }
      });

      setFilterSelection(remainingSelections);
    } else {
      setFilterSelection(selectedCategories);
    }
  }, [selectedCategories]);

  const { data: zonesData, isLoading: isZonesLoading } = useGetZones(locationSelection?.map((location) => parseInt(location)) || []);
  const zoneOptions = useMemo(() => zonesData || [], [zonesData]);

  useEffect(() => {
    if (zonesData && zonesData.length > 0) {
      const allZoneIds = zonesData.map((zone) => zone.id.toString());
      setZoneSelection(allZoneIds);
    }
  }, [zonesData]);

  const [loyaltySelection, setLoyaltySelection] = useState(() => loyaltyOptions.map((option) => option.value));
  const [ageSelection, setAgeSelection] = useState(() => ageOptions.map((option) => option.value));
  const [genderSelection, setGenderSelection] = useState(() => genderOptions.map((option) => option.value));

  const { metricsData, isLoading: isMetricsLoading } = useMetricsData(
    selectedDateRange,
    locationSelection,
    userProfile.user_id,
    isLocationsSuccess,
    loyaltySelection,
    ageSelection,
    genderSelection,
    zoneSelection
  );

  // Memoize metric tile data calculation
  const metricTileDataOne = useMemo(() => [
    {
      name: "Guest connections",
      metric: metricsData?.guest_connections ?? "Loading...",
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(metricsData?.disable_before_date))
        ? "The last period does not have data"
        : `${metricsData?.last_period_gc_change >= 0 ? "+" : ""}${metricsData?.last_period_gc_change ?? "..."}% since last period`,
    },
    {
      name: "Unique guests",
      metric: metricsData?.unique_guests ?? "Loading...",
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(metricsData?.disable_before_date))
        ? "The last period does not have data"
        : `${metricsData?.last_period_ug_change >= 0 ? "+" : ""}${metricsData?.last_period_ug_change ?? "..."}% since last period`,
    },
    {
      name: "Avg. duration",
      metric: `${metricsData?.average_connection_duration ?? "Loading..."} mins`,
      updates: isEqual(startOfDay(selectedDateRange.from), startOfDay(metricsData?.disable_before_date))
        ? "The last period does not have data"
        : `${metricsData?.last_period_acd_change >= 0 ? "+" : ""}${metricsData?.last_period_acd_change ?? "..."}% since last period`,
    },
  ], [metricsData, selectedDateRange.from, selectedDateRange.to]);

  // Memoize date calculations
  const chartTypesToShow = useMemo(() => {
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const normalizedFrom = startOfDay(selectedDateRange.from);
    const normalizedTo = startOfDay(selectedDateRange.to);
    const days = Math.ceil((normalizedTo.getTime() - normalizedFrom.getTime()) / oneDayInMilliseconds) + 1;

    if (days === 1) {
      return ["timeOfDay"];
    } else if (days > 1 && days < 62) {
      return ["day", "timeOfDay", "dayOfWeek"];
    } else if (days >= 62) {
      return ["month", "day", "timeOfDay", "dayOfWeek"];
    }
    
    return [];
  }, [selectedDateRange.from, selectedDateRange.to]);

  // Memoize chart visibility checks
  const chartVisibility = useMemo(() => ({
    areAllAgesDisabled: !metricsData?.connection_details?.length || 
      !metricsData.connection_details.some((conn) => conn.age !== "field_disabled"),
    areAllGendersDisabled: !metricsData?.connection_details?.length || 
      !metricsData.connection_details.some((conn) => conn.gender !== "field_disabled")
  }), [metricsData?.connection_details]);

  useEffect(() => {
    console.log("disabled", metricsData?.disable_before_date);
    if (metricsData?.disable_before_date) {
      setDisableBefore(metricsData.disable_before_date);
    }
  }, [metricsData]);

  useEffect(() => {
    if (!metricsData?.disable_before_date || !selectedDateRange?.from || !selectedDateRange?.to) {
      return;
    }

    const availableFromDate = startOfDay(new Date(metricsData.disable_before_date));
    const currentFromDate = startOfDay(selectedDateRange.from);
    const currentToDate = selectedDateRange.to;

    if (!isAfter(availableFromDate, currentFromDate)) {
      return;
    }

    setSelectedDateRange({
      from: availableFromDate,
      to: isAfter(availableFromDate, currentToDate)
        ? endOfDay(availableFromDate)
        : currentToDate,
    });
  }, [metricsData?.disable_before_date, selectedDateRange, setSelectedDateRange]);

  const [activeTab, setActiveTab] = useState("connections");

  const handleDisplayByChange = useCallback((newValue) => {
    setDisplayBy((prevValue) => (prevValue === newValue ? null : newValue));
  }, []);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
  }, []);

  const isLoading = isBrandsLoading || isZonesLoading || !isLocationsSuccess;

  // Showing loading progress bar if any data is loading
  if (isLoading) {
    return (
      <div style={{ 
        position: "fixed", 
        top: 0, 
        left: 0, 
        width: "100%", 
        zIndex: 1000 
      }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  return (
    <Flex className="content-container" direction="column" gap="5"
      style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
      {/* Show metric loading progress bar at top when metrics are loading */}
      {isMetricsLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      {/* Header Section */}
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start",
        minHeight: 0, width: "100%" }}>

        {/* Segment and Filter Section */}
        <Flex justify="between" align="center" gap="2" style={{ alignSelf: "stretch", flexWrap: "wrap" }} >
          {/* Left Section */}
          <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
            <SegmentedControl.Root defaultValue="connections" onValueChange={(value) => handleTabChange(value)}>
              <SegmentedControl.Item value="connections">Connections</SegmentedControl.Item>
              <SegmentedControl.Item value="durations">Avg. Duration</SegmentedControl.Item>
            </SegmentedControl.Root>
          </Flex>
          {/* Right Section */}
          <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
            <DisplayBySelect
              options={displayOptions}
              value={displayBy}
              onValueChange={(newValue) => {
                handleDisplayByChange(newValue);
              }}
              placeholder="Display by"
            />
            <FilterByMultiSelect options={filterOptions} defaultValue={selectedCategories} onValueChange={setSelectedCategories} />
            <DatePickerWithRange value={selectedDateRange} onDateChange={setSelectedDateRange} disableBeforeDate={disableBefore} />
            {zoneOptions.length > 0 && 
              <ZonesMultiSelect options={zoneOptions} defaultValue={zoneSelection} onValueChange={setZoneSelection} />
            }
            {/* Use filtered location options when brand filter is active */}
            <LocationMultiSelect 
              options={showBrandFilter ? filteredLocationOptions : initialItems} 
              defaultValue={locationSelection} 
              onValueChange={setLocationSelection} 
            />
            {showBrandFilter && 
              <BrandMultiSelect 
                options={formattedBrandOptions} 
                defaultValue={brandSelection} 
                onValueChange={setBrandSelection} 
              />
            }
          </Flex>
        </Flex>
        {/* Filter display Selection */}
        {filterSelection.length > 0 && (
          <Flex className="filter-bar" style={{ alignSelf: "stretch" }} justify="end" align="center" gap="2">
            <Text weight="regular" size="2" style={{ color: 'var(--gray-12)'}}>Filter</Text>
            {filterSelection.includes("guest_status") && (
              <MultiSelect
                options={loyaltyOptions}
                defaultValue={loyaltyOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setLoyaltySelection(value);
                }}
                placeholder="Customer loyalty"
              />
            )}
            {filterSelection.includes("age") && (
              <MultiSelect
                options={ageOptions}
                defaultValue={ageOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setAgeSelection(value);
                }}
                placeholder="Age"
              />
            )}
            {filterSelection.includes("gender") && (
              <MultiSelect
                options={genderOptions}
                defaultValue={genderOptions.map((option) => option.value)}
                onValueChange={(value) => {
                  setGenderSelection(value);
                }}
                placeholder="Gender"
              />
            )}
          </Flex>
        )}
      </Flex>

      {/* Analytics Section */}
      {!metricsData?.connection_details?.length ? (
        <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
          {/* No results default section */}
          <Flex p="5" direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center",
              justifyContent: "center", width: "100%", borderRadius: "8px", border: "1px dashed var(--gray-a6)",
              flex: 1, position: "relative", backgroundColor: "var(--color-background)" }}>
            <Flex direction="column" align="center" gap="2" maxWidth="400px" style={{alignItems: 'center', width: '100%'}}>
              <Text size="4" weight="medium" align="center">No results</Text>
              <Text size="2" weight="regular" align="center" color="gray">
                Adjust filters or check if you have an active Wi-Fi integrations for your organisation.
              </Text>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", flex: "1 0 0", alignItems: "center",
            overflowY: "scroll" }}>
          {/* Metric Cards */}
          <Flex className="card-container" style={{ alignSelf: "stretch" }} align="start" gap="5">
            <LiveConnectionsCard metric={liveConnectionStat?.value} />
            {metricTileDataOne.map((entry, index) => (
              <MetricCard 
                key={index} 
                metricName={entry.name}
                metric={entry.metric}
                updates={entry.updates} />
            ))}
          </Flex>

          {/* Charts */}
          {activeTab === "connections" && (
            <>
              <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center" }}>
                {chartTypesToShow.map((chartType) => {
                  const { text, ChartComponent } = connectionsChartConfig[chartType];
                  return (
                    <ChartCard key={chartType} text={text}>
                      <ChartComponent
                        data={{ data: metricsData }}
                        startTimeMilliseconds={selectedDateRange.from.getTime()}
                        endTimeMilliseconds={selectedDateRange.to.getTime()}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  );
                })}
              </Flex>

              <Flex display="flex" direction="row" align="start" gap="5" width="100%">
                {!chartVisibility.areAllAgesDisabled && (
                  <Suspense>
                    <ChartCard text="Unique guests by age">
                      <UniqueGuestsByAgeChart
                        metricsData={metricsData}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  </Suspense>
                )}
                {!chartVisibility.areAllGendersDisabled && (
                  <Suspense>
                    <ChartCard text="Unique guests by gender">
                      <UniqueGuestsByGenderChart
                        metricsData={metricsData}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  </Suspense>
                )}
              </Flex>
            </>
          )}
          {activeTab === "durations" && (
            <>
              <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center" }}>
                {chartTypesToShow.map((chartType) => {
                  const { text, ChartComponent } = durationsChartConfig[chartType];
                  return (
                    <ChartCard key={chartType} text={text}>
                      <ChartComponent
                        data={{ data: metricsData }}
                        startTimeMilliseconds={selectedDateRange.from.getTime()}
                        endTimeMilliseconds={selectedDateRange.to.getTime()}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  );
                })}
              </Flex>

              <Flex display="flex" direction="row" align="start" gap="5" width="100%">
                {!chartVisibility.areAllAgesDisabled && (
                  <Suspense>
                    <ChartCard text="Average duration of guest connections by age">
                      <AverageDurationByAgeChart
                        metricsData={metricsData}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  </Suspense>
                )}
                {!chartVisibility.areAllGendersDisabled && (
                  <Suspense>
                    <ChartCard text="Average duration of guest connections by gender">
                      <AverageDurationByGenderChart
                        metricsData={metricsData}
                        displayBy={displayBy}
                      />
                    </ChartCard>
                  </Suspense>
                )}
              </Flex>
            </>
          )}
        </Flex>
      )}
    </Flex>
  )
};
